"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiImage } from "react-icons/fi";
import Image, { getImageProps } from "next/image";
import { useCatalogImageLayout } from "@/contexts/catalog-image-layout-provider";
import { canOptimizeImage, resolveImageUrl } from "@/lib/image-utils";
import { cx } from "@/lib/tailwind-utils";
import { ShopPhotoTransition } from "./shop-photo-transition";
const SWIPE_THRESHOLD_PX = 40;
const PAGE_IMAGE_SIZES = "(min-width: 768px) 50vw, 100vw";
const preloadedPagePhotos = new Set<string>();
interface ShopItemGalleryProps {
  images: string[];
  alt: string;
  /** Storefront product pages pass the item id so the photo morphs from the shop grid. */
  transitionItemId?: string;
  variant?: "page" | "dialog";
  className?: string;
}

/** Product photos: a main image with thumbnails, arrow keys, and swipe on touch screens. */
export function ShopItemGallery(t0) {
  const $ = _c(97);
  const {
    images,
    alt,
    transitionItemId,
    variant: t1,
    className
  } = t0;
  const variant = t1 === undefined ? "page" : t1;
  const {
    catalogImageLayout
  } = useCatalogImageLayout();
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef(null);
  const count = images.length;
  let t2;
  if ($[0] !== images) {
    t2 = images.join("|");
    $[0] = images;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const imagesKey = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setActiveIndex(0);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== imagesKey) {
    t4 = [imagesKey];
    $[3] = imagesKey;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useEffect(t3, t4);
  if (count === 0) {
    if (variant === "dialog") {
      return null;
    }
    let t5;
    if ($[5] !== className) {
      t5 = cx("flex aspect-square w-full items-center justify-center rounded-2xl bg-background-secondary text-foreground-muted", className);
      $[5] = className;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    let t7;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <FiImage aria-hidden={true} className="h-10 w-10" />;
      t7 = <span className="sr-only">No photo</span>;
      $[7] = t6;
      $[8] = t7;
    } else {
      t6 = $[7];
      t7 = $[8];
    }
    let t8;
    if ($[9] !== t5) {
      t8 = <div className={t5}>{t6}{t7}</div>;
      $[9] = t5;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    return t8;
  }
  const index = Math.min(activeIndex, count - 1);
  let t5;
  if ($[11] !== count) {
    t5 = next => setActiveIndex((next + count) % count);
    $[11] = count;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const show = t5;
  const hasMany = count > 1;
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[13] !== alt || $[14] !== catalogImageLayout || $[15] !== className || $[16] !== count || $[17] !== hasMany || $[18] !== images || $[19] !== index || $[20] !== show || $[21] !== transitionItemId || $[22] !== variant) {
    const mainSrc = resolveImageUrl(images[index]);
    let t22;
    if ($[41] !== hasMany || $[42] !== index || $[43] !== show) {
      t22 = event => {
        if (!hasMany) {
          return;
        }
        if (event.key === "ArrowRight") {
          event.preventDefault();
          show(index + 1);
        } else {
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            show(index - 1);
          }
        }
      };
      $[41] = hasMany;
      $[42] = index;
      $[43] = show;
      $[44] = t22;
    } else {
      t22 = $[44];
    }
    const handleKeyDown = t22;
    let t23;
    if ($[45] !== hasMany || $[46] !== index || $[47] !== show) {
      t23 = event_0 => {
        const start = touchStartX.current;
        touchStartX.current = null;
        const end = event_0.changedTouches[0]?.clientX;
        if (start === null || end === undefined || !hasMany) {
          return;
        }
        const delta = end - start;
        if (Math.abs(delta) < SWIPE_THRESHOLD_PX) {
          return;
        }
        show(delta < 0 ? index + 1 : index - 1);
      };
      $[45] = hasMany;
      $[46] = index;
      $[47] = show;
      $[48] = t23;
    } else {
      t23 = $[48];
    }
    const handleTouchEnd = t23;
    if ($[49] !== className) {
      t20 = cx("flex flex-col gap-3", className);
      $[49] = className;
      $[50] = t20;
    } else {
      t20 = $[50];
    }
    t21 = "shop-item-gallery";
    t12 = "region";
    t13 = "carousel";
    t14 = `${alt} photos`;
    t15 = hasMany ? 0 : undefined;
    t16 = handleKeyDown;
    if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = event_1 => {
        touchStartX.current = event_1.touches[0]?.clientX ?? null;
      };
      $[51] = t17;
    } else {
      t17 = $[51];
    }
    t18 = handleTouchEnd;
    if ($[52] !== catalogImageLayout || $[53] !== variant) {
      t19 = cx("group relative w-full touch-pan-y overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background", variant === "page" ? "aspect-square rounded-2xl bg-background" : cx("bg-background-secondary", catalogImageLayout === "SQUARE" ? "h-64 md:h-80" : "h-52 md:h-64"));
      $[52] = catalogImageLayout;
      $[53] = variant;
      $[54] = t19;
    } else {
      t19 = $[54];
    }
    T1 = ShopPhotoTransition;
    t11 = index === 0 ? transitionItemId : undefined;
    T0 = Image;
    t7 = mainSrc;
    t8 = hasMany ? `${alt}, photo ${index + 1} of ${count}` : alt;
    t9 = true;
    t10 = variant === "page" ? PAGE_IMAGE_SIZES : "(min-width: 768px) 640px, 100vw";
    t6 = canOptimizeImage(mainSrc);
    $[13] = alt;
    $[14] = catalogImageLayout;
    $[15] = className;
    $[16] = count;
    $[17] = hasMany;
    $[18] = images;
    $[19] = index;
    $[20] = show;
    $[21] = transitionItemId;
    $[22] = variant;
    $[23] = T0;
    $[24] = T1;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
    $[37] = t6;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
  } else {
    T0 = $[23];
    T1 = $[24];
    t10 = $[25];
    t11 = $[26];
    t12 = $[27];
    t13 = $[28];
    t14 = $[29];
    t15 = $[30];
    t16 = $[31];
    t17 = $[32];
    t18 = $[33];
    t19 = $[34];
    t20 = $[35];
    t21 = $[36];
    t6 = $[37];
    t7 = $[38];
    t8 = $[39];
    t9 = $[40];
  }
  const t22 = !t6;
  const t23 = variant === "page" ? "object-contain" : catalogImageLayout === "SQUARE" ? "object-contain" : "object-cover";
  let t24;
  if ($[55] !== t23) {
    t24 = cx("h-full w-full", t23);
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== T0 || $[58] !== t10 || $[59] !== t22 || $[60] !== t24 || $[61] !== t7 || $[62] !== t8 || $[63] !== t9) {
    t25 = <T0 src={t7} alt={t8} fill={t9} sizes={t10} unoptimized={t22} loading="eager" fetchPriority="high" className={t24} data-testid="shop-item-gallery-main" />;
    $[57] = T0;
    $[58] = t10;
    $[59] = t22;
    $[60] = t24;
    $[61] = t7;
    $[62] = t8;
    $[63] = t9;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== T1 || $[66] !== t11 || $[67] !== t25) {
    t26 = <T1 itemId={t11}>{t25}</T1>;
    $[65] = T1;
    $[66] = t11;
    $[67] = t25;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] !== count || $[70] !== hasMany || $[71] !== index || $[72] !== show) {
    t27 = hasMany && <><button type="button" aria-label="Previous photo" onClick={() => show(index - 1)} className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background/90 text-foreground shadow-md transition-opacity hover:bg-background focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100"><FiChevronLeft aria-hidden={true} /></button><button type="button" aria-label="Next photo" onClick={() => show(index + 1)} className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background/90 text-foreground shadow-md transition-opacity hover:bg-background focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100"><FiChevronRight aria-hidden={true} /></button><p aria-live="polite" className="absolute bottom-3 right-3 rounded-full bg-background/90 px-2.5 py-0.5 text-caption-strong text-foreground">{index + 1} / {count}</p></>;
    $[69] = count;
    $[70] = hasMany;
    $[71] = index;
    $[72] = show;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== t12 || $[75] !== t13 || $[76] !== t14 || $[77] !== t15 || $[78] !== t16 || $[79] !== t17 || $[80] !== t18 || $[81] !== t19 || $[82] !== t26 || $[83] !== t27) {
    t28 = <div role={t12} aria-roledescription={t13} aria-label={t14} tabIndex={t15} onKeyDown={t16} onTouchStart={t17} onTouchEnd={t18} className={t19}>{t26}{t27}</div>;
    $[74] = t12;
    $[75] = t13;
    $[76] = t14;
    $[77] = t15;
    $[78] = t16;
    $[79] = t17;
    $[80] = t18;
    $[81] = t19;
    $[82] = t26;
    $[83] = t27;
    $[84] = t28;
  } else {
    t28 = $[84];
  }
  let t29;
  if ($[85] !== count || $[86] !== hasMany || $[87] !== images || $[88] !== index || $[89] !== show || $[90] !== variant) {
    t29 = hasMany && <ul className={cx("flex gap-2 overflow-x-auto", variant === "dialog" && "px-6 md:px-8")} aria-label="Choose a photo">{images.map((url, thumbIndex) => <li key={`${url}-${thumbIndex}`} className="flex-none"><button type="button" onClick={() => show(thumbIndex)} aria-label={`Show photo ${thumbIndex + 1} of ${count}`} aria-current={thumbIndex === index ? "true" : undefined} className={cx("block overflow-hidden rounded-lg border-2 bg-background-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary", variant === "page" ? "h-16 w-16 md:h-20 md:w-20" : "h-12 w-12", thumbIndex === index ? "border-primary" : "border-transparent opacity-70 hover:opacity-100")}><Image src={resolveImageUrl(url)} alt="" width={80} height={80} unoptimized={!canOptimizeImage(resolveImageUrl(url))} className="h-full w-full object-cover" /></button></li>)}</ul>;
    $[85] = count;
    $[86] = hasMany;
    $[87] = images;
    $[88] = index;
    $[89] = show;
    $[90] = variant;
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  let t30;
  if ($[92] !== t20 || $[93] !== t21 || $[94] !== t28 || $[95] !== t29) {
    t30 = <div className={t20} data-testid={t21}>{t28}{t29}</div>;
    $[92] = t20;
    $[93] = t21;
    $[94] = t28;
    $[95] = t29;
    $[96] = t30;
  } else {
    t30 = $[96];
  }
  return t30;
}

/** Starts loading the photo a Storefront product page opens on, so the photo transition has it when the page appears. */
export function preloadShopItemPagePhoto(item: {
  imageUrl: string | null;
  galleryImageUrls?: string[];
}) {
  const url = shopItemImages(item)[0];
  if (!url || preloadedPagePhotos.has(url)) return;
  preloadedPagePhotos.add(url);
  const src = resolveImageUrl(url);
  const {
    props
  } = getImageProps({
    src,
    alt: "",
    fill: true,
    sizes: PAGE_IMAGE_SIZES,
    unoptimized: !canOptimizeImage(src)
  });
  const image = new window.Image();
  image.onerror = () => preloadedPagePhotos.delete(url);
  if (props.sizes) image.sizes = props.sizes;
  if (props.srcSet) image.srcset = props.srcSet;
  image.src = props.src;
}

/** The gallery for an item, falling back to its single cover image. */
export function shopItemImages(item: {
  imageUrl: string | null;
  galleryImageUrls?: string[];
}): string[] {
  if (item.galleryImageUrls && item.galleryImageUrls.length > 0) {
    return item.galleryImageUrls;
  }
  return item.imageUrl ? [item.imageUrl] : [];
}